import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import axios from "axios";
import QuizProvider from "context/QuizContext";
import UserInfoProvider from "context/UserInfoContext";
import { AnimatePresence } from "framer-motion";
import useInactivityRedirect from "hooks/useInactivityRedirect";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import DataScreenSaver from "routes/DataScreenSaver/DataScreenSaver";
import EndScreen from "routes/EndScreen/EndScreen";
import Explanation from "routes/Explanation/Explanation";
import Intro from "routes/Intro/Intro";
import QuestionairePicker from "routes/QuestionairePicker/QuestionairePicker";
import Quiz from "routes/Quiz/Quiz";
import Age from "routes/UserInfo/Age";
import ExtraUserInfo from "routes/UserInfo/ExtraUserInfo";
import PickSubjects from "routes/PickSubjects/PickSubjects";
import PostCode from "routes/UserInfo/PostalCode";
import "styles/App.scss";

const rootRoute = createRootRoute();

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Intro,
});

const explanationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/uitleg",
  component: Explanation,
});

const postCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/postcode",
  component: PostCode,
});

const extraUserInfoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/extra_user_info",
  component: ExtraUserInfo,
});

const questionairePickerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/questionaire-picker",
  component: QuestionairePicker,
});

const ageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/age",
  component: Age,
});

const dataScreenSaverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/data-screen-saver",
  component: DataScreenSaver,
});

const quizRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quiz",
  component: Quiz,
});

const pickSubjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pick-subjects",
  component: PickSubjects,
});

const endRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/end",
  component: EndScreen,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  explanationRoute,
  extraUserInfoRoute,
  postCodeRoute,
  questionairePickerRoute,
  ageRoute,
  dataScreenSaverRoute,
  quizRoute,
  endRoute,
  pickSubjectsRoute,
]);

const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default behavior is to have a stale time of 0. This means that the query will be refetched on every mount.
      staleTime: 10 * 1000 * 60,
    },
  },
});

//set base URL
axios.defaults.baseURL = import.meta.env.VITE_API_URL;

const InactivityRedirectWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => /*useInactivityRedirect();*/ {
  return <>{children}</>;
};

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
  }
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

// Render the app
const rootElement = document.getElementById("app")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <UserInfoProvider>
          <QuizProvider>
            <AnimatePresence mode="wait">
              <InactivityRedirectWrapper>
                <RouterProvider
                  router={router}
                  key={router.routesById.toString()}
                />
              </InactivityRedirectWrapper>
            </AnimatePresence>
          </QuizProvider>
        </UserInfoProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
